import { toZonedParts } from '@/features/scheduler/schedulerDates'
import { formatCalendarDay } from '@/lib/utils/format'
import type { PipelineState } from '@/types/models'

/**
 * When the studio's day is unknown the browser's is the last resort — these
 * lines only ever render inside a signed-in session, where `today` is set.
 */
function dayOf(today: string | null): Date | undefined {
  return today ? new Date(`${today}T12:00:00`) : undefined
}

/**
 * A Vertriebsvorgang's next step in one line: "Wiedervorlage Morgen, 14:00 ·
 * Versuch 2 heute, 5 insgesamt".
 *
 * The backend sends this block with every lead, call outcome and booking, and
 * it is the only place these fields appear — the Anrufsliste row is a flatter
 * shape with its own `dueAtLabel`. So the answer to "when do I call this person
 * again?" arrives once, in the response to the action that changed it, which is
 * why it belongs in the toast that reports the action.
 *
 * Every value here is computed server-side. Nothing is re-derived: whether a
 * further attempt is allowed today is `blockedForToday`, never a count compared
 * against the cap.
 */
export function pipelineSummary(
  pipeline: PipelineState | null,
  timezone: string,
  today: string | null,
): string | null {
  if (!pipeline) return null

  // A closed process has no next step, and its counters and dates would only
  // invite someone to keep working a lead that is done.
  if (pipeline.salesProcessClosed) return 'Vertriebsvorgang abgeschlossen'

  const parts: string[] = []
  const now = dayOf(today)

  if (pipeline.restPhaseUntil) {
    // No time of day: the boundary is `rest_phase_started_at + 14 days`, and
    // printing 00:00 beside it would dress a calculation up as a decision.
    const { date } = toZonedParts(pipeline.restPhaseUntil, timezone)
    parts.push(`Ruhephase bis ${formatCalendarDay(date, now)}`)
  } else if (pipeline.nextActionAt) {
    const { date, time } = toZonedParts(pipeline.nextActionAt, timezone)
    parts.push(`Wiedervorlage ${formatCalendarDay(date, now)}, ${time}`)
  } else if (pipeline.dueToday) {
    parts.push('Heute fällig')
  }

  if (pipeline.blockedForToday) {
    parts.push('Heute keine weiteren Versuche')
  } else if (pipeline.attemptsToday > 0) {
    parts.push(`Versuch ${pipeline.attemptsToday} heute, ${pipeline.attemptCount} insgesamt`)
  } else if (pipeline.attemptCount > 0) {
    parts.push(`${pipeline.attemptCount} Anrufe bisher`)
  }

  // Worth saying out loud: the automatic rule is not what put the lead here, so
  // the stage will not move on its own either.
  if (pipeline.manualOverride) parts.push('Phase manuell gesetzt')

  return parts.length > 0 ? parts.join(' · ') : null
}
